import React, { useCallback, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import MinimalisticTable, { ColumnDef } from '@/shared/components/ui/MinimalisticTable';
import type { OutcomeDefinition } from '../api/outcomeDefinitionsApi';
import { deleteOutcomeDefinitionApi, fetchOutcomeDefinitionsV2Api, OutcomeDefinitionsV2Response } from '../api/outcomeDefinitionsApi';

const QUERY_KEY = ['admin', 'orchestrator', 'outcome-definitions', 'v2'] as const;

type ScopedOutcomeDefinitionRow = OutcomeDefinition & { handler_key_base: string };

export default function OutcomesRegistryPanelV2() {
  const queryClient = useQueryClient();
  const [pendingDelete, setPendingDelete] = useState<{ handler_key: string; key: string } | null>(null);

  const { data, isLoading, error } = useQuery<OutcomeDefinitionsV2Response, Error>({
    queryKey: QUERY_KEY,
    queryFn: () => fetchOutcomeDefinitionsV2Api(),
  });

  const deleteMutation = useMutation({
    mutationFn: ({ handler_key, key }: { handler_key: string; key: string }) =>
      deleteOutcomeDefinitionApi(handler_key, key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setPendingDelete(null);
    },
  });

  const handleDelete = useCallback((handlerKey: string, key: string) => {
    setPendingDelete({ handler_key: handlerKey, key });
  }, []);

  const confirmDelete = useCallback(() => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete);
  }, [pendingDelete, deleteMutation]);

  const globalDefs = data?.global ?? [];
  const runDefs = data?.run ?? [];

  const handlerScopedRows: ScopedOutcomeDefinitionRow[] = useMemo(() => {
    const groups = data?.handler_scoped ?? [];
    const rows: ScopedOutcomeDefinitionRow[] = [];
    for (const g of groups) {
      const base = String(g.handler_key_base ?? '').trim();
      for (const d of g.definitions ?? []) {
        rows.push({ ...d, handler_key_base: base });
      }
    }
    rows.sort((a, b) => {
      const hb = a.handler_key_base.localeCompare(b.handler_key_base);
      if (hb !== 0) return hb;
      const hk = String(a.handler_key ?? '').localeCompare(String(b.handler_key ?? ''));
      if (hk !== 0) return hk;
      const k = String(a.key ?? '').localeCompare(String(b.key ?? ''));
      if (k !== 0) return k;
      const cat = (a.id_category ?? 0) - (b.id_category ?? 0);
      if (cat !== 0) return cat;
      return (a.id_type ?? 0) - (b.id_type ?? 0);
    });
    return rows;
  }, [data]);

  const columnsBase: ColumnDef<OutcomeDefinition>[] = useMemo(() => [
    {
      header: 'Handler Key',
      accessor: 'handler_key',
      Cell: (v: string) => <span className="text-xs break-all">{v?.trim() ? v : '—'}</span>,
    },
    {
      header: 'Key',
      accessor: 'key',
      Cell: (v: string) => <span className="text-xs break-all">{v?.trim() ? v : '—'}</span>,
    },
    { header: 'Kind', accessor: 'value_kind' },
    {
      header: 'Description',
      accessor: 'description',
      Cell: (v: string) => <span className="text-xs break-words">{v?.trim() ? v : '—'}</span>,
    },
    { header: 'Category', accessor: 'id_category' },
    { header: 'Type', accessor: 'id_type' },
    {
      header: '',
      accessor: '' as keyof OutcomeDefinition,
      sortable: false,
      Cell: (_: any, row: OutcomeDefinition) => (
        <button
          type="button"
          className="text-xs text-neutral-600 hover:text-rose-400 transition-colors px-1"
          title={`Delete ${row.handler_key} / ${row.key}`}
          onClick={(e) => { e.stopPropagation(); handleDelete(row.handler_key, row.key); }}
        >
          ✕
        </button>
      ),
    },
  ], [handleDelete]);

  const columnsScoped: ColumnDef<ScopedOutcomeDefinitionRow>[] = useMemo(() => [
    {
      header: 'Handler Key Base',
      accessor: 'handler_key_base',
      Cell: (v: string) => <span className="text-xs break-all">{v?.trim() ? v : '—'}</span>,
    },
    ...columnsBase as unknown as ColumnDef<ScopedOutcomeDefinitionRow>[],
  ], [columnsBase]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm text-neutral-400">
          v2 view: global + run-level contracts are overlays; handler-scoped contracts are grouped by handler key base.
        </span>
      </div>

      {isLoading && <p className="text-sm text-neutral-400">Loading outcomes…</p>}
      {error && (
        <p className="text-sm text-rose-400">
          Failed to load outcomes: {error.message}
        </p>
      )}

      {!isLoading && !error && (
        <div className="space-y-6">
          <div className="space-y-2">
            <div className="text-xs text-neutral-500">Global (`__global__`) — applies to every workflow step.</div>
            <div className="pac-glass rounded-md p-2">
              <MinimalisticTable<OutcomeDefinition>
                columns={columnsBase}
                data={[...globalDefs]}
                noDataMessage="No global outcome definitions found."
                showSearch
                searchPlaceholder="Search global outcomes…"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-xs text-neutral-500">Run (`__run__`) — applies to workflow-run outcomes (not per-step).</div>
            <div className="pac-glass rounded-md p-2">
              <MinimalisticTable<OutcomeDefinition>
                columns={columnsBase}
                data={[...runDefs]}
                noDataMessage="No run outcome definitions found."
                showSearch
                searchPlaceholder="Search run outcomes…"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-xs text-neutral-500">Handler-scoped — definitions grouped by handler key base.</div>
            <div className="pac-glass rounded-md p-2">
              <MinimalisticTable<ScopedOutcomeDefinitionRow>
                columns={columnsScoped}
                data={handlerScopedRows}
                noDataMessage="No handler-scoped outcome definitions found."
                showSearch
                searchPlaceholder="Search handler-scoped outcomes…"
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirmation ──────────────────────────────── */}
      {pendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="pac-glass rounded-lg p-5 max-w-md w-full space-y-4">
            <p className="text-sm text-neutral-200">
              Delete outcome definition?
            </p>
            <div className="text-xs text-neutral-400 space-y-1">
              <div><span className="text-neutral-500">Handler:</span> {pendingDelete.handler_key}</div>
              <div><span className="text-neutral-500">Key:</span> {pendingDelete.key}</div>
            </div>
            <p className="text-xs text-rose-400/80">
              This also removes all outcome rows stored under this definition.
            </p>
            {deleteMutation.isError && (
              <p className="text-xs text-rose-400">
                {(deleteMutation.error as Error)?.message || 'Delete failed'}
              </p>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                className="px-3 py-1.5 text-xs rounded pac-glass-btn text-neutral-300 transition-colors"
                onClick={() => setPendingDelete(null)}
                disabled={deleteMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-xs rounded bg-rose-600/80 text-white hover:bg-rose-500 transition-colors"
                onClick={confirmDelete}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
